import { useEffect, useId, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/Button';

import { isCoachAvailable, resumeIndex, visibleCoachSteps } from '../coach';
import { continueCoach } from '../coaching';
import type { GuidanceInternals } from '../context';
import { fixedBottomInset } from '../engine/dom';
import { useGuidanceState } from '../store';

/**
 * "Continue?" (WC-09): a coach that was RUNNING when the page went away — a
 * reload, an expired session, a new sign-in, the MFA step — comes back as this
 * small card, minimised. It never continues by itself and never takes focus
 * (a blur on the cart saves): it is a labelled region the person reaches in
 * reading order, with Continue and Not now.
 *
 * Continue starts a RESUMED run: what happened before the reload is not
 * assumed; the step is decided by the page and the server-rendered probes.
 * Not now keeps the coach in progress (Help & tours offers Continue) and drops
 * only the "running" mark.
 */
export function CoachResume({ guidance }: { guidance: GuidanceInternals }) {
  const { t } = useTranslation();
  const headingId = useId();
  const { panelOpen, active } = useGuidanceState(guidance.store);
  // Re-render when stored progress changes.
  useSyncExternalStore(guidance.progress.subscribe, guidance.progress.get, guidance.progress.get);
  const bottom = useBottomInset();

  const { ctx, progress } = guidance;
  const def = (guidance.coaches ?? []).find(
    (coach) => progress.coachActive(coach) && isCoachAvailable(coach, ctx),
  );

  if (def === undefined || active !== null || panelOpen || progress.isMemoryOnly()) return null;

  const title = t(def.titleKey);
  const steps = visibleCoachSteps(def, ctx);
  const at = resumeIndex(steps, progress.coachResumeStep(def));

  return (
    <section
      aria-labelledby={headingId}
      data-guidance-dock="resume"
      style={{ bottom: bottom + 16 }}
      className="fixed end-4 z-20 w-[min(22rem,calc(100vw-2rem))] rounded-(--radius-card) border border-line-subtle bg-card p-4 shadow-(--shadow-lg)"
    >
      <h2 id={headingId} className="type-card-title text-ink-heading">
        {t('guidance.coaches.resume.title', { title })}
      </h2>
      <p className="type-meta mt-1 text-ink-muted">
        {t('guidance.coaches.resume.body', { current: at + 1, total: steps.length })}
      </p>
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            progress.setCoach(def, 'in_progress', { step: progress.coachResumeStep(def) })
          }
        >
          {t('guidance.coaches.resume.later')}
        </Button>
        <Button
          size="sm"
          aria-label={t('guidance.help.continueNamed', { title })}
          onClick={() => continueCoach(guidance, def.id)}
        >
          {t('guidance.help.continue')}
        </Button>
      </div>
    </section>
  );
}

/** Above the fixed bars along the bottom edge (the phone's navigation), kept current on resize. */
function useBottomInset(): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const measure = () => setInset(fixedBottomInset());

    measure();
    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
  }, []);

  return inset;
}
